/**
 * Horse Race SFX module — mp3-backed buffer cache, mirrors the WhatIs
 * pattern. Logical cue methods load mp3s from src/assets/sfx and route
 * through audioBus.sfxGain().
 *
 * Cues:
 *   bell    — race start (BETTING -> RACE)
 *   chime   — betting opens / game over
 *   gallop  — short hoof-beat tick; played as a loop during the race
 *   cheer   — finish line / photo finish
 *   bet     — per-bet blip (throttled by main.ts)
 *
 * The gallop loop is exposed as `sfx.startGallopLoop()` /
 * `sfx.stopGallopLoop()` — uses a buffer-source.loop=true so multiple bets
 * can't stack a thousand looping sources (one source, one stop).
 */

import bellUrl from '../assets/sfx/bell.mp3?url';
import chimeUrl from '../assets/sfx/chime.mp3?url';
import gallopUrl from '../assets/sfx/gallop.mp3?url';
import cheerUrl from '../assets/sfx/cheer.mp3?url';
import betUrl from '../assets/sfx/bet.mp3?url';
import crowdAmbienceUrl from '../assets/sfx/crowd-ambience.mp3?url';
// Countdown beeps + starting pistol — queued in tools/asset-queue.json.
// Vite resolves `?url` against the disk path; if the file isn't present yet
// the fetch in loadBuffer() will 404 and we fall back to the synthesized
// WebAudio cue. Once the queue completes the next deploy picks them up.
import countdownBeepUrl from '../assets/sfx/countdown-beep.mp3?url';
import startPistolUrl from '../assets/sfx/start-pistol.mp3?url';

import { audioBus } from './bus.js';

export type SfxName =
  | 'bell'
  | 'chime'
  | 'gallop'
  | 'cheer'
  | 'bet'
  | 'crowd'
  | 'countdown'
  | 'pistol';

const SFX_URL_BELL = bellUrl;
const SFX_URL_CHIME = chimeUrl;
const SFX_URL_GALLOP = gallopUrl;
const SFX_URL_CHEER = cheerUrl;
const SFX_URL_BET = betUrl;
const SFX_URL_CROWD = crowdAmbienceUrl;
const SFX_URL_COUNTDOWN = countdownBeepUrl;
const SFX_URL_PISTOL = startPistolUrl;

function urlFor(name: SfxName): string {
  if (name === 'bell') return SFX_URL_BELL;
  if (name === 'chime') return SFX_URL_CHIME;
  if (name === 'gallop') return SFX_URL_GALLOP;
  if (name === 'cheer') return SFX_URL_CHEER;
  if (name === 'crowd') return SFX_URL_CROWD;
  if (name === 'countdown') return SFX_URL_COUNTDOWN;
  if (name === 'pistol') return SFX_URL_PISTOL;
  return SFX_URL_BET;
}

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = urlFor(name);
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[hr-sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
    console.log(`[hr-sfx] play ${name} dur=${buf.duration.toFixed(2)}s ctx=${ctx.state}`);
  } catch (err) {
    console.warn(`[hr-sfx] play ${name} failed:`, err);
  }
}

function play(name: SfxName): void {
  const ctx = audioBus.context();
  if (!ctx) {
    console.warn(`[hr-sfx] play ${name} skipped: no AudioContext`);
    return;
  }
  const buf = buffers.get(name);
  if (!buf) {
    console.log(`[hr-sfx] play ${name} pending buffer load`);
    void loadBuffer(name).then((b) => {
      if (!b) return;
      // If the bus is still locked at the time the buffer resolves we wait
      // on unlock() before firing — otherwise the suspended context drops
      // the source on the floor without producing sound.
      if (!audioBus.isUnlocked()) {
        void audioBus.unlock().then((ok) => { if (ok) playBuffer(ctx, b, name); });
      } else {
        playBuffer(ctx, b, name);
      }
    });
    return;
  }
  if (!audioBus.isUnlocked()) {
    // Resume first so the buffer source actually outputs audio. The previous
    // implementation fire-and-forget the unlock and called playBuffer
    // immediately; if the AudioContext was still suspended at .start(0) the
    // source got dropped silently — explaining the "I see the meter move
    // but hear nothing" symptom.
    void audioBus.unlock().then((ok) => { if (ok) playBuffer(ctx, buf, name); });
    return;
  }
  playBuffer(ctx, buf, name);
}

// --- Gallop loops (race phase) ----------------------------------------------
// Per-horse looping sources keyed by lane (1..8); finish-line stop fades each
// out individually. Key 0 = legacy shared-loop fallback (vol 0.4).
const SHARED_GALLOP_KEY = 0;
interface GallopLoop {
  source: AudioBufferSourceNode;
  gain: GainNode;
}
const gallopLoops = new Map<number, GallopLoop>();

function startGallopLoopFor(key: number, targetGain = 0.2, fadeMs = 250): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  if (gallopLoops.has(key)) return; // already running for this lane
  const buf = buffers.get('gallop');
  if (!buf) {
    void loadBuffer('gallop').then(() => startGallopLoopFor(key, targetGain, fadeMs));
    return;
  }
  // If the bus is still locked the source.start would happen on a suspended
  // context and drop. Resume first, then start the loop.
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock().then((ok) => { if (ok) startGallopLoopFor(key, targetGain, fadeMs); });
    return;
  }
  try {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.connect(audioBus.sfxGain());
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = true;
    source.connect(gain);
    // Per-horse offset so 8 simultaneous loops don't phase-lock into a single
    // synchronized hoofbeat — staggers the seam by 1/8 of the buffer length.
    const offset = key > 0 ? (buf.duration * (key - 1)) / 8 : 0;
    source.start(0, offset);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(0.001, targetGain),
      ctx.currentTime + fadeMs / 1000,
    );
    gallopLoops.set(key, { source, gain });
    console.log(`[hr-sfx] gallop loop[${key}] started target=${targetGain} ctx=${ctx.state}`);
  } catch (err) {
    console.warn(`[hr-sfx] gallop loop[${key}] start failed:`, err);
  }
}

function stopGallopLoopFor(key: number, fadeMs = 400): void {
  const ctx = audioBus.context();
  const loop = gallopLoops.get(key);
  if (!ctx || !loop) return;
  gallopLoops.delete(key);
  try {
    const now = ctx.currentTime;
    loop.gain.gain.cancelScheduledValues(now);
    loop.gain.gain.setValueAtTime(loop.gain.gain.value || 0.0001, now);
    loop.gain.gain.exponentialRampToValueAtTime(0.0001, now + fadeMs / 1000);
    setTimeout(() => {
      try { loop.source.stop(); } catch { /* ignore */ }
    }, fadeMs + 50);
  } catch (err) {
    console.warn(`[hr-sfx] gallop loop[${key}] stop failed:`, err);
  }
}

function startGallopLoop(targetGain = 0.4, fadeMs = 250): void {
  startGallopLoopFor(SHARED_GALLOP_KEY, targetGain, fadeMs);
}

function stopGallopLoop(fadeMs = 400): void {
  // Stop ALL gallop loops (shared + per-horse) so a scene transition never
  // leaves a stray hoofbeat looping after the race is over.
  for (const key of Array.from(gallopLoops.keys())) {
    stopGallopLoopFor(key, fadeMs);
  }
}

// --- Crowd ambience loop (race phase) ---------------------------------------
// Real crowd-ambience.mp3 (~30s, broadcast-quality stadium murmur) layered
// under the gallop loop during the race. Same pattern as the gallop loop:
// dedicated GainNode + exponential fades so we can stop cleanly when the
// race ends. cheer.mp3 remains the one-shot finish-line sting.
let crowdSource: AudioBufferSourceNode | null = null;
let crowdGain: GainNode | null = null;

function startCrowdLoop(targetGain = 0.35, fadeMs = 600): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  if (crowdSource) return; // already running
  const buf = buffers.get('crowd');
  if (!buf) {
    void loadBuffer('crowd').then(() => startCrowdLoop(targetGain, fadeMs));
    return;
  }
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock().then((ok) => { if (ok) startCrowdLoop(targetGain, fadeMs); });
    return;
  }
  try {
    crowdGain = ctx.createGain();
    crowdGain.gain.setValueAtTime(0.0001, ctx.currentTime);
    crowdGain.connect(audioBus.sfxGain());
    crowdSource = ctx.createBufferSource();
    crowdSource.buffer = buf;
    crowdSource.loop = true;
    crowdSource.connect(crowdGain);
    crowdSource.start(0);
    crowdGain.gain.exponentialRampToValueAtTime(
      Math.max(0.001, targetGain),
      ctx.currentTime + fadeMs / 1000,
    );
    console.log(`[hr-sfx] crowd loop started target=${targetGain} ctx=${ctx.state}`);
  } catch (err) {
    console.warn('[hr-sfx] crowd loop start failed:', err);
    crowdSource = null;
    crowdGain = null;
  }
}

function stopCrowdLoop(fadeMs = 600): void {
  const ctx = audioBus.context();
  if (!ctx || !crowdSource || !crowdGain) return;
  try {
    const now = ctx.currentTime;
    crowdGain.gain.cancelScheduledValues(now);
    crowdGain.gain.setValueAtTime(crowdGain.gain.value || 0.0001, now);
    crowdGain.gain.exponentialRampToValueAtTime(0.0001, now + fadeMs / 1000);
    const src = crowdSource;
    setTimeout(() => {
      try { src.stop(); } catch { /* ignore */ }
    }, fadeMs + 50);
  } catch (err) {
    console.warn('[hr-sfx] crowd loop stop failed:', err);
  } finally {
    crowdSource = null;
    crowdGain = null;
  }
}

// --- Countdown / pistol fallbacks ------------------------------------------
// The countdown-beep.mp3 + start-pistol.mp3 are queued via tools/asset-queue.json
// but won't exist on disk until the asset generator finishes. Until then we
// synthesize the cues in WebAudio: a short 880Hz sine for the tick, and a
// loud noise burst (white noise + low-end thump) for the pistol.
//
// Once the real mp3s land, loadBuffer() resolves successfully and `play()`
// dispatches via the buffer path; these synth fallbacks are never reached.

function synthBeep(): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock().then((ok) => { if (ok) synthBeep(); });
    return;
  }
  try {
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t0);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.45, t0 + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
    osc.connect(gain);
    gain.connect(audioBus.sfxGain());
    osc.start(t0);
    osc.stop(t0 + 0.22);
  } catch (err) {
    console.warn('[hr-sfx] synthBeep failed:', err);
  }
}

function synthPistol(): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock().then((ok) => { if (ok) synthPistol(); });
    return;
  }
  try {
    const t0 = ctx.currentTime;
    // 350ms of white noise, exponentially decaying. Adds a thumpy low-end
    // sweep with a sharp transient for the "crack".
    const noiseLen = Math.floor(ctx.sampleRate * 0.35);
    const buf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseLen, 2);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.85, t0);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
    noise.connect(noiseGain);
    noiseGain.connect(audioBus.sfxGain());
    noise.start(t0);
    noise.stop(t0 + 0.4);
    // Low-end thump for body.
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, t0);
    osc.frequency.exponentialRampToValueAtTime(40, t0 + 0.18);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.0001, t0);
    oscGain.gain.exponentialRampToValueAtTime(0.6, t0 + 0.005);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
    osc.connect(oscGain);
    oscGain.connect(audioBus.sfxGain());
    osc.start(t0);
    osc.stop(t0 + 0.3);
    // Layer the bell as a fallback "starting pistol" cue too — gives the
    // user the existing race-start sting while the real mp3 generates.
    play('bell');
  } catch (err) {
    console.warn('[hr-sfx] synthPistol failed:', err);
  }
}

/**
 * Play `name`, falling back to `synth` when the buffer isn't loaded (e.g.
 * the queued mp3 hasn't generated yet). The buffer load is kicked off in the
 * background so subsequent calls use the real mp3 once it lands.
 */
function playWithFallback(name: SfxName, synth: () => void): void {
  if (buffers.get(name)) {
    play(name);
    return;
  }
  // Fire the synth immediately for instant audio feedback.
  synth();
  // Try to load the real buffer in the background — next call will use it
  // once it lands. If the file doesn't exist (404), buffers.set(name, null)
  // and we keep falling back to the synth.
  if (!inflight.has(name) && !buffers.has(name)) {
    void loadBuffer(name);
  }
}

export const sfx = {
  async preload(): Promise<void> {
    const names: SfxName[] = ['bell', 'chime', 'gallop', 'cheer', 'bet', 'crowd', 'countdown', 'pistol'];
    await Promise.all(names.map((n) => loadBuffer(n)));
  },
  play,
  bell(): void { play('bell'); },
  chime(): void { play('chime'); },
  cheer(): void { play('cheer'); },
  bet(): void { play('bet'); },
  gallop(): void { play('gallop'); },
  /** Countdown tick — 5/4/3/2/1. Falls back to a synthesized 880Hz beep
   *  while the real mp3 hasn't generated yet. */
  countdown(): void { playWithFallback('countdown', synthBeep); },
  /** Starting-pistol crack on GO!. Falls back to a layered noise+thump+bell
   *  while the real mp3 hasn't generated yet. */
  pistol(): void { playWithFallback('pistol', synthPistol); },
  startGallopLoop,
  stopGallopLoop,
  startGallopLoopFor,
  stopGallopLoopFor,
  startCrowdLoop,
  stopCrowdLoop,
};
